/**
 * Removes OpenAI citation markers from assistant text.
 *
 * The backend resolves these markers into links to the help center before it
 * sends or stores a message (see ai/citations.py), so this is here for messages
 * that were stored before that existed, and as a backstop if a new marker shape
 * shows up that the server does not recognise yet.
 *
 * Ported from the reference implementation in
 * https://developers.openai.com/api/docs/guides/citation-formatting
 */
const CITATION_START = '\uE200';
const CITATION_DELIMITER = '\uE202';
const CITATION_STOP = '\uE201';

// The guide only documents the "cite" family, but file_search emits "filecite"
// and its siblings use their own prefixes.
const FAMILY_PATTERN = '[a-z]*cite';
const SOURCE_KINDS = 'file|search|block|url|news|image|video|audio|product';

const SOURCE_ID_RE = /^[A-Za-z0-9_-]+$/;
const LINE_LOCATOR_RE = /^L\d+(?:-L\d+)?$/;

const CITATION_RE = new RegExp(
  `${CITATION_START}(${FAMILY_PATTERN})${CITATION_DELIMITER}([\\s\\S]*?)${CITATION_STOP}`,
  'g',
);

// A marker the model opened and never closed. Each field has to look like a
// source id or a line locator so the pattern cannot run on into the answer text.
const UNCLOSED_MARKER_RE = new RegExp(
  `${CITATION_START}(?:${FAMILY_PATTERN})?`
  + `(?:${CITATION_DELIMITER}(?:turn\\d+(?:${SOURCE_KINDS})\\d+|L\\d+(?:-L\\d+)?)?)*`,
  'g',
);

// Markers that lost their delimiters somewhere, e.g. "fileciteturn0file2turn0file6".
// "cite" alone is an ordinary word, so a family name only counts as debris when
// it carries a source-kind prefix.
const DEGRADED_MARKER_RE = new RegExp(
  `(?:file|video|image|audio|screenshot|product|nav)cite`
  + `(?:turn\\d+(?:${SOURCE_KINDS})\\d+)*(?:L\\d+(?:-L\\d+)?)?`,
  'g',
);
const DEGRADED_REFERENCE_RE = new RegExp(
  `(?:cite)?(?:turn\\d+(?:${SOURCE_KINDS})\\d+)+(?:L\\d+(?:-L\\d+)?)?`,
  'g',
);

const ORPHAN_DELIMITER_RE = /[\uE200-\uE20F]/g;

export function extractCitations(text) {
  if (!text || !text.includes(CITATION_START)) return [];

  const citations = [];
  for (const match of text.matchAll(CITATION_RE)) {
    const parts = match[2]
        .split(CITATION_DELIMITER)
        .map(part => part.trim())
        .filter(Boolean);
    if (parts.length === 0) continue;

    if (LINE_LOCATOR_RE.test(parts[parts.length - 1])) parts.pop();
    if (parts.length === 0 || parts.some(part => !SOURCE_ID_RE.test(part))) continue;

    citations.push({
      raw: match[0],
      family: match[1],
      sourceIds: parts,
      start: match.index,
      end: match.index + match[0].length,
    });
  }
  return citations;
}

export function stripCitations(text, citations) {
  let cleaned = text;
  const ordered = Array.from(citations).sort((left, right) => right.start - left.start);
  for (const citation of ordered) {
    cleaned = cleaned.slice(0, citation.start) + cleaned.slice(citation.end);
  }
  return cleaned;
}

export function stripCitationMarkers(text) {
  if (!text) return text;

  let cleaned = stripCitations(text, extractCitations(text));
  cleaned = cleaned.replace(UNCLOSED_MARKER_RE, '');
  cleaned = cleaned.replace(DEGRADED_MARKER_RE, '');
  cleaned = cleaned.replace(DEGRADED_REFERENCE_RE, '');
  cleaned = cleaned.replace(ORPHAN_DELIMITER_RE, '');
  if (cleaned === text) return text;

  // Removing a marker strands the space that used to sit in front of it.
  cleaned = cleaned.replace(/[ \t]+([.,;:!?)\]])/g, '$1');
  return cleaned.replace(/[ \t]+(?=\n|$)/g, '');
}
